import { useEffect, useState, useRef, useCallback } from "react";
import "../App.css";
import CharacterCustomizer from "./CharacterCustomizer";
import { Box } from "@mui/material";
import { BOOK_BUILDER, CARD_BUILDER, PLAYMAT_BUILDER } from "../constants/common";
import { BOOK_BUILDER_URL, CARD_BUILDER_URL, PLAYMAT_BUILDER_URL } from "../constants/domains";

const Dashboard = () => {
  const myRef = useRef(null);
  const MAX_PAGES = 4;

  const [mainCharacter, setMainCharacter] = useState({});
  const [partnerCharacter, setPartnerCharacter] = useState({});
  const [page, setPage] = useState(1);
  const [isDataLoadedFromCookies, setIsDataLoadedFromCookies] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState({
    base: false,
    eyes: false,
    hair: false,
    outfit: false,
  });

  const setQueryParams = () => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const destination = urlParams.get("destination");
    const orderId = urlParams.get("order_id");
    const playmatSize = urlParams.get("playmat_size");

    if(playmatSize) {
      sessionStorage.setItem("playmat_size", playmatSize);
    }

    sessionStorage.setItem("order_id", orderId);
    sessionStorage.setItem("destination", destination);
  };

  useEffect(() => {
    const cookieData = localStorage.getItem("charactersData");

    if (cookieData) {
      const parsedCookieData = JSON.parse(cookieData);
      if (parsedCookieData.mainCharacter != null) {
        setMainCharacter(parsedCookieData.mainCharacter);
      }
      if (parsedCookieData.partnerCharacter != null) {
        setPartnerCharacter(parsedCookieData.partnerCharacter);
      }
    }
    setQueryParams();
    setIsDataLoadedFromCookies(true);
  }, []);

  const saveToCookie = useCallback(() => {
    const charactersData = JSON.stringify({
      mainCharacter,
      partnerCharacter,
    });
    localStorage.setItem("charactersData", charactersData);
  }, [mainCharacter, partnerCharacter, isDataLoadedFromCookies]);

  const goToPrevPage = () => {
    setPage(page - 1);
    saveToCookie();
    if (page - 1 === 2) {
      scrollToRef();
    }
  };

  const goToNextPage = () => {
    setPage(page + 1);
    saveToCookie();
    scrollToRef();
  };

  useEffect(() => {
    if (isDataLoadedFromCookies) {
      saveToCookie();
    }
  }, [mainCharacter, partnerCharacter, isDataLoadedFromCookies]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  const scrollToRef = () => {
    myRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const getDestinationValue = () => {
    const destination = sessionStorage.getItem("destination");
    return {
      [BOOK_BUILDER]: BOOK_BUILDER_URL,
      [CARD_BUILDER]: CARD_BUILDER_URL,
      [PLAYMAT_BUILDER]: PLAYMAT_BUILDER_URL
    }[destination];
  };

  const goToNextApp = () => {
    const playmatSize = sessionStorage.getItem("playmat_size");

    const playmatSizePart = playmatSize ? `&playmatSize=${encodeURIComponent(playmatSize)}` : "";

    const destination = getDestinationValue();
    const mainName = `?mainName=${encodeURIComponent(mainCharacter.name)}`;
    const mainBase = `&mainBase=${encodeURIComponent(mainCharacter.base)}`;
    const mainEyes = `&mainEyes=${encodeURIComponent(mainCharacter.eyes)}`;
    const mainHair = `&mainHair=${encodeURIComponent(mainCharacter.hair)}`;
    const mainOutfit = `&mainOutfit=${encodeURIComponent(mainCharacter.outfit)}`;
    // const mainFacialHair = mainCharacter.sex === "male" ? `&mainFacialHair=${encodeURIComponent(mainCharacter.facialHair)}` : "";
    // const mainGlasses = mainCharacter.sex === "male" ? `&mainGlasses=${encodeURIComponent(mainCharacter.glasses)}` : "";
    const mainSex = `&mainSex=${encodeURIComponent(mainCharacter.sex)}`;

    const partnerName = `&partnerName=${encodeURIComponent(partnerCharacter.name)}`;
    const partnerBase = `&partnerBase=${encodeURIComponent(partnerCharacter.base)}`;
    const partnerEyes = `&partnerEyes=${encodeURIComponent(partnerCharacter.eyes)}`;
    const partnerHair = `&partnerHair=${encodeURIComponent(partnerCharacter.hair)}`;
    const partnerOutfit = `&partnerOutfit=${encodeURIComponent(partnerCharacter.outfit)}`;
    // const partnerFacialHair =
    //   partnerCharacter.sex === "male" ? `&partnerFacialHair=${encodeURIComponent(partnerCharacter.facialHair)}` : "";
    // const partnerGlasses = partnerCharacter.sex === "male" ? `&partnerGlasses=${encodeURIComponent(partnerCharacter.glasses)}` : "";
    const partnerSex = `&partnerSex=${encodeURIComponent(partnerCharacter.sex)}`;
    const orderId = `&order_id=${sessionStorage.getItem("order_id")}`;
    const mainUrlPart = `${mainName}${mainBase}${mainEyes}${mainHair}${mainOutfit}${mainSex}`;
    const partnerUrlPart = `${partnerName}${partnerBase}${partnerEyes}${partnerHair}${partnerOutfit}${partnerSex}`;

    const url = `${destination}${mainUrlPart}${partnerUrlPart}${orderId}${playmatSizePart}`;
    window.location.href = url;
  };

  return (
    <section id="CharacterCreator" style={{ overflowX: "hidden" }}>
      <Box className="main-container" ref={myRef} />
      {page !== 5 && (
        <Box className="ellipse-container">
          {Array.from({ length: page }, (_, i) => (
            <Box className="ellipse-green" key={`${i}`} />
          ))}
          {Array.from({ length: MAX_PAGES - page }, (_, i) => (
            <Box className="ellipse-white" key={`${i}`} />
          ))}
        </Box>
      )}
      <br />
      {page === 1 ? (
        <Box>
          {isDataLoadedFromCookies ? (
            <CharacterCustomizer
              imagesLoaded={imagesLoaded}
              setImagesLoaded={setImagesLoaded}
              action={setPartnerCharacter}
              data={partnerCharacter}
              gender="female"
              pageNumber="01"
              label="your Partner"
              description="Let's start by customizing your partner. Click the options at the bottom of this screen to select the traits that will make it look like them."
            />
          ) : (
            <p />
          )}
          <Box className="buttons-container">
            <p
              id="save-and-continue-1"
              className="main-continue-button"
              onClick={() => {
                goToNextPage();
              }}
            >
              Save and Continue
            </p>
          </Box>
        </Box>
      ) : (
        <Box>
          {isDataLoadedFromCookies ? (
            <CharacterCustomizer
              imagesLoaded={imagesLoaded}
              setImagesLoaded={setImagesLoaded}
              action={setMainCharacter}
              data={mainCharacter}
              isDataLoadedFromCookies={isDataLoadedFromCookies}
              gender="male"
              pageNumber="02"
              label="yourself"
              description="Now let's customize yourself. Select the traits that represent you the best!"
            />
          ) : (
            <p />
          )}
          <Box className="orderButtons">
            <p id="back-1" className="main-back-button" onClick={goToPrevPage}>
              Back
            </p>
            <p id="save-and-continue-2" className="main-continue-button" onClick={() => goToNextApp()}>
              Save and Continue
            </p>
          </Box>
        </Box>
      )}
    </section>
  );
};

export default Dashboard;
